import { Button, Divider, Modal } from "@mui/material";
import { ReactElement, createContext, useContext, useState } from "react";

// import { useTranslation } from "react-i18next";

const context = createContext<{
    confirm: (content: ConfirmOptions) => void;
} | null>(null);

export type ConfirmOptions = {
    title?: string | ReactElement;
    message?: string | ReactElement;
    cancel: () => void;
    actions: {
        label: string;
        onClick: () => void;
        color?: "primary" | "error";
        variant?: "contained" | "outlined";
    }[];
};

export const ConfirmProvider = ({ children }: { children: ReactElement }) => {
    const [config, setConfig] = useState<ConfirmOptions | null>(null);

    // const { t } = useTranslation();

    const openConfirm = (config: ConfirmOptions | null) => {
        if (!config) {
            setConfig(null);
        } else if (config) {
            setConfig(config);
        }
    };

    const handleClose = () => openConfirm(null);

    return (
        <context.Provider
            value={{
                confirm: openConfirm,
            }}
        >
            {children}
            <Modal
                open={!!config}
                onClose={() => {
                    config?.cancel();
                    handleClose();
                }}
                className="flex justify-center items-center"
            >
                <div
                    className={`flex flex-col bg-white min-w-50 max-w-80 border-slate-700 border-2 border-solid outline-none shadow-lg rounded-3xl overflow-hidden p-4`}
                >
                    <div className="font-lg font-bold">{config?.title}</div>
                    {config?.title && <Divider sx={{ my: 1 }} />}
                    <div className="font-md">{config?.message}</div>
                    <Divider sx={{ my: 1 }} />

                    <div className="flex justify-end gap-2">
                        {config?.actions.map((action, index) => (
                            <Button
                                key={index}
                                onClick={() => {
                                    action.onClick();
                                    handleClose();
                                }}
                                variant={action.variant || "outlined"}
                                color={action.color}
                            >
                                {action.label}
                            </Button>
                        ))}
                    </div>
                </div>
            </Modal>
        </context.Provider>
    );
};

const useConfirm = () => {
    const ctx = useContext(context);
    if (!ctx) {
        throw new Error("useConfirm must be used within a Confirm Provider");
    } else {
        return ctx;
    }
};

export default useConfirm;
